import type { InvivoBackgroundDescriptor, InvivoBackgroundTextureDescriptor } from "../types/invivo_scene";

function filenameExtension(url: string): string | undefined {
  const pathname = new URL(url).pathname;
  const match = pathname.match(/\.[^./]+$/);

  return match?.[0];
}

function requireAsset<T>(value: T | null | undefined, message: string): T {
  if (value === undefined || value === null) {
    throw new Error(message);
  }

  return value;
}

function buildCubeTexture({ scene, url }: { scene: unknown; url: string }): unknown {
  const { BABYLON } = window;

  return BABYLON.CubeTexture?.CreateFromPrefilteredData(url, scene, filenameExtension(url));
}

function buildTexture({
  scene,
  textureDescriptor,
}: {
  scene: unknown;
  textureDescriptor: InvivoBackgroundTextureDescriptor;
}): unknown {
  const { BABYLON } = window;

  if (!BABYLON.Texture) {
    return undefined;
  }

  const texture = new BABYLON.Texture(
    textureDescriptor.url,
    scene,
    false,
    textureDescriptor.invertY ?? false
  );
  texture.uScale = textureDescriptor.uScale ?? 1;
  texture.vScale = textureDescriptor.vScale ?? 1;

  return texture;
}

function buildBackgroundTexture({
  scene,
  descriptor,
}: {
  scene: unknown;
  descriptor: InvivoBackgroundTextureDescriptor;
}): unknown {
  const texture =
    descriptor.kind === "cube"
      ? buildCubeTexture({
          scene,
          url: descriptor.url,
        })
      : buildTexture({
          scene,
          textureDescriptor: descriptor,
        });

  return requireAsset(texture, `Missing required background asset "${descriptor.url}".`);
}

export function configureBackgroundRuntime({
  scene,
  background,
}: {
  scene: {
    environmentTexture?: unknown;
  };
  background: InvivoBackgroundDescriptor;
}): {
  enabled: boolean;
  material?: unknown;
} {
  const { BABYLON } = window;

  if (!BABYLON.StandardMaterial) {
    return {
      enabled: background.enabled,
    };
  }

  const material = new BABYLON.StandardMaterial("Invivo::background::material", scene);
  material.disableLighting = true;
  material.reflectionTexture = null;
  material.ambientTexture = null;
  material.emissiveTexture = null;

  if (background.emissiveColor) {
    material.emissiveColor = BABYLON.Color3?.FromHexString(background.emissiveColor);
  }

  if (background.ambientTexture) {
    material.ambientTexture = buildBackgroundTexture({
      scene,
      descriptor: background.ambientTexture,
    });
  }

  if (background.reflectionTexture) {
    material.reflectionTexture = buildBackgroundTexture({
      scene,
      descriptor: background.reflectionTexture,
    });
    if (background.reflectionTexture.coordinatesMode && material.reflectionTexture) {
      material.reflectionTexture.coordinatesMode = BABYLON.Texture?.[background.reflectionTexture.coordinatesMode];
    }
  }

  scene.environmentTexture = background.environmentTexture
    ? buildBackgroundTexture({
        scene,
        descriptor: background.environmentTexture,
      })
    : null;

  material.freeze?.();

  return {
    enabled: background.enabled,
    material,
  };
}
